import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Efectivo, MovimientoSocio } from '../../../models/finanzas/efectivo/efectivo.model';
import { EfectivoService } from '../../../services/finanzas/efectivo/efectivo.service';
import { consts } from '../../../consts';
import { ActualSelected, Utils } from '../../../shared/utils';
import { ActivityService } from '../../user/activity/activity.service';
import { MasterService } from '../../../modules_v2/master/services/master.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../modules_v2/shared/components/confirm-dialog.component';
import {
  currentActivityOption,
  EntityFormOption,
  fromApiDate,
  InjectedOptions,
  optionId,
} from '../../../modules_v2/finance/entity-form/entity-form.utils';

export interface EfectivoDialogMovimientoSocioData {
  efectivo: Efectivo;
  tipoMovimientoSocioId: number;
  editUid?: string;
}

@Component({
    selector: 'convfact-efectivomovimientosocio',
    templateUrl: './efectivo-dialog-movimiento-socio.component.html',
    styleUrls: ['./efectivo-dialog.component.css'],
    standalone: false
})
export class EfectivoDialogMovimientoSocioComponent implements OnInit {

  public efectivo: Efectivo;

  public tipoMovimientoSocioId: number;

  public tittle = 'Elemento';

  public ready = false;
  public loadingList = false;
  public loadError = false;
  public saving = false;
  public savingMessage = '';
  public submitted = false;
  public saveError = '';

  public editing: MovimientoSocio | null = null;

  public partners: EntityFormOption[] = [];
  public activities: EntityFormOption[] = [];
  public dataSource: MovimientoSocio[] = [];

  public readonly year = ActualSelected.yearSelected;

  public form: UntypedFormGroup;

  private model: MovimientoSocio;
  private changesAtModal = false;
  private requestedEditDone = false;
  private readonly injected = new InjectedOptions();

  static open(dialog: MatDialog, data: EfectivoDialogMovimientoSocioData): Observable<boolean> {
    return dialog.open<EfectivoDialogMovimientoSocioComponent, EfectivoDialogMovimientoSocioData, boolean>(EfectivoDialogMovimientoSocioComponent, {
      width: '720px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<EfectivoDialogMovimientoSocioComponent, boolean>,
    public efectivoService: EfectivoService,
    @Inject(MAT_DIALOG_DATA) public data: EfectivoDialogMovimientoSocioData,
    public activityService: ActivityService,
    private masterService: MasterService,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.form = this.fb.group({
      fecha: [new Date()],
      concepto: [''],
      importe: [null],
      comisiones: [null],
      partner: [null],
      activity: [null],
    });
  }

  get isEditMode(): boolean {
    return !!this.editing;
  }

  get isReintegro(): boolean {
    return this.tipoMovimientoSocioId === 1;
  }

  get subtitle(): string {
    const parts = [this.efectivo?.nombre, this.efectivo?.accountingModelsNumber ? 'Cuenta ' + this.efectivo.accountingModelsNumber : null];
    return parts.filter(Boolean).join(' · ');
  }

  get elementName(): string {
    return this.isReintegro ? 'reintegro' : 'ingreso';
  }

  get totalImporte(): number {
    return Utils.RoundTwoDecimals(this.dataSource.reduce((acc, item) => acc + (Number(item.importe) || 0), 0));
  }

  get totalComisiones(): number {
    return Utils.RoundTwoDecimals(this.dataSource.reduce((acc, item) => acc + (Number(item.comisiones) || 0), 0));
  }

  get conceptoLength(): number {
    return String(this.form.get('concepto').value || '').length;
  }

  get missingDate(): boolean { return !this.form.get('fecha').value; }
  get missingConcept(): boolean { return !String(this.form.get('concepto').value || '').trim(); }
  get missingImporte(): boolean { return !Number(this.form.get('importe').value); }
  get missingActivity(): boolean { return optionId(this.form.get('activity').value) === null; }
  get missingPartner(): boolean { return optionId(this.form.get('partner').value) === null; }

  public control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  public ngOnInit(): void {
    this.efectivo = this.data.efectivo;
    this.tipoMovimientoSocioId = this.data.tipoMovimientoSocioId;
    this.model = this.emptyMovimiento();

    if (this.tipoMovimientoSocioId === 1) {
      this.tittle = 'Reintegro del socio';
    } else if (this.tipoMovimientoSocioId === 2) {
      this.tittle = 'Ingreso del socio';
    }

    this.load();
  }

  public retry(): void {
    this.loadError = false;
    this.load();
  }

  public save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';

    if (this.missingDate || this.missingConcept || this.missingImporte || this.missingActivity || this.missingPartner) {
      return;
    }

    const wasEdit = this.isEditMode;
    const payload = this.buildPayload();

    this.changesAtModal = true;
    this.saving = true;
    this.savingMessage = wasEdit ? 'Actualizando movimiento…' : 'Añadiendo movimiento…';
    this.dialogRef.disableClose = true;

    const request = wasEdit
      ? this.efectivoService.movimientoSocioUpdate(payload)
      : this.efectivoService.movimientoSocioCreate(payload);

    request
      .then(() => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.notify.ok(`${this.tittle} ${wasEdit ? 'actualizado' : 'añadido'} correctamente`);
        if (!wasEdit || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.resetForm();
        this.reloadList();
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = (error && error.error && error.error.message)
          || (wasEdit ? 'Ha ocurrido un error inesperado al actualizar el movimiento socio' : 'Ha ocurrido un error inesperado al añadir el movimiento socio');
      });
  }

  public delete(item: MovimientoSocio): void {
    if (this.saving) return;
    this.confirm.open({
      title: `Eliminar ${this.tittle.toLowerCase()}`,
      subtitle: this.subtitle || undefined,
      message: `Se eliminará el ${this.elementName} del socio y su asiento contable. Esta acción no se puede deshacer.`,
      tone: 'danger',
      details: this.details(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando movimiento…',
      errorText: 'Ha ocurrido un error al eliminar el movimiento socio',
      action: () => this.efectivoService.movimientoSocioDelete(item.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changesAtModal = true;
      this.notify.ok(`${this.tittle} eliminado correctamente`);
      if (this.editing && this.editing.uid === item.uid) this.resetForm();
      this.reloadList();
    });
  }

  public edit(item: MovimientoSocio): void {
    if (this.saving) return;
    this.editing = item;
    this.submitted = false;
    this.saveError = '';
    this.injected.clear();

    this.model = { ...item };

    this.form.patchValue({
      fecha: fromApiDate(item.fecha),
      concepto: item.concepto || '',
      importe: item.importe != null ? Number(item.importe) : null,
      comisiones: item.comisiones != null ? Number(item.comisiones) : null,
      partner: this.injected.ensure(this.partners, item.partnersId, item.partnersName),
      activity: this.injected.ensure(this.activities, item.activitiesUid, item.activitiesName),
    }, { emitEvent: false });
  }

  public cancelEdit(): void {
    this.resetForm();
  }

  public closeDialog(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changesAtModal);
  }

  public trackByUid(_: number, item: MovimientoSocio): string { return item.uid; }

  private load(): void {
    this.ready = false;
    Promise.all([
      this.loadActivities(),
      this.loadPartners(),
      this.listRequest(),
    ])
      .then(([activities, partners, list]) => {
        this.activities = activities;
        this.partners = partners;
        this.dataSource = list;
        this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private reloadList(): void {
    this.loadingList = true;
    this.listRequest()
      .then(list => {
        this.dataSource = list;
        this.openRequestedEdit();
      })
      .catch(() => { this.notify.error('No se pudieron recargar los movimientos del socio'); })
      .then(() => { this.loadingList = false; });
  }

  private listRequest(): Promise<MovimientoSocio[]> {
    return this.efectivoService.movimientoSocioList(this.efectivo.uid, this.tipoMovimientoSocioId)
      .then(response => Array.isArray(response) ? response : []);
  }

  private loadActivities(): Promise<EntityFormOption[]> {
    return this.activityService.getList()
      .then(list => (list || [])
        .filter(activity => activity && activity.uid && activity.uid !== consts.EMPTY_GUID)
        .map(activity => ({ id: activity.uid, display: activity.name || '' })))
      .catch(() => [] as EntityFormOption[]);
  }

  private loadPartners(): Promise<EntityFormOption[]> {
    return firstValueFrom(this.masterService.getParnerts())
      .then(list => (list || [])
        .filter(partner => partner && partner.id !== null && partner.id !== undefined)
        .map(partner => ({ id: Number(partner.id), display: partner.display || '' })))
      .catch(() => [] as EntityFormOption[]);
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const row = this.dataSource.find(item => item.uid === this.data.editUid);
    if (row) this.edit(row);
  }

  private resetForm(): void {
    this.editing = null;
    this.injected.clear();
    this.submitted = false;
    this.saveError = '';
    this.model = this.emptyMovimiento();
    this.form.reset({
      fecha: new Date(),
      concepto: '',
      importe: null,
      comisiones: null,
      partner: null,
      activity: null,
    }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  private applySingleOptionDefaults(): void {
    if (this.editing) return;

    if (this.missingActivity) {
      const current = currentActivityOption(this.activities);
      if (current) this.control('activity').setValue(current, { emitEvent: false });
      else if (this.activities.length === 1) this.control('activity').setValue(this.activities[0], { emitEvent: false });
    }
    if (this.missingPartner && this.partners.length === 1) {
      this.control('partner').setValue(this.partners[0], { emitEvent: false });
    }
  }

  private buildPayload(): MovimientoSocio {
    const value = this.form.getRawValue();
    return {
      ...this.model,
      efectivoId: this.efectivo.uid,
      tiposMovimientoSocioId: this.model.tiposMovimientoSocioId || this.tipoMovimientoSocioId,
      fecha: value.fecha,
      concepto: String(value.concepto || '').trim(),
      importe: Number(value.importe) || 0,
      comisiones: this.isReintegro ? (Number(value.comisiones) || 0) : 0,
      partnersId: optionId(value.partner),
      activitiesUid: optionId(value.activity),
    };
  }

  private emptyMovimiento(): MovimientoSocio {
    return {
      fecha: new Date(),
      comisiones: 0,
      concepto: '',
      importe: 0,
      efectivoId: '0',
      partnersId: 0,
      partnersName: '',
      tiposMovimientoSocioId: this.data.tipoMovimientoSocioId,
      uid: '',
      activitiesUid: consts.EMPTY_GUID,
      activitiesName: '',
    };
  }

  private details(item: MovimientoSocio): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (item.fecha) details.push({ label: 'Fecha', value: formatDate(item.fecha, 'dd/MM/yyyy', 'es-ES') });
    if (item.concepto) details.push({ label: 'Concepto', value: item.concepto });
    if (item.partnersName) details.push({ label: 'Socio', value: item.partnersName });
    details.push({ label: 'Importe', value: Utils.FormatMoney(Number(item.importe) || 0) + ' €' });
    return details;
  }
}
